export type LogLevel = 'info' | 'warn' | 'error';

export interface LogEntry {
  level: LogLevel;
  timestamp: string;
  conversationId?: string;
  message: string;
  metadata?: Record<string, unknown>;
}

class Telemetry {
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  log(
    level: LogLevel,
    conversationId: string | undefined,
    message: string,
    metadata?: Record<string, unknown>,
  ): void {
    const entry: LogEntry = {
      level,
      timestamp: new Date().toISOString(),
      conversationId,
      message,
      metadata: this.sanitizeMetadata(metadata),
    };

    this.logs.push(entry);

    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Also log to console in development
    if (typeof console !== 'undefined') {
      const logFn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      const safeMetadata = entry.metadata;
      logFn(
        `[Fluxbot Widget] ${message}`,
        safeMetadata && Object.keys(safeMetadata).length > 0 ? safeMetadata : '',
      );
    }
  }

  info(conversationId: string | undefined, message: string, metadata?: Record<string, unknown>): void {
    this.log('info', conversationId, message, metadata);
  }

  warn(conversationId: string | undefined, message: string, metadata?: Record<string, unknown>): void {
    this.log('warn', conversationId, message, metadata);
  }

  error(conversationId: string | undefined, message: string, metadata?: Record<string, unknown>): void {
    this.log('error', conversationId, message, metadata);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clear(): void {
    this.logs = [];
  }

  private sanitizeMetadata(metadata?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!metadata) {
      return undefined;
    }

    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(metadata)) {
      // Skip user data, message content, etc.
      if (
        !key.includes('message') &&
        !key.includes('content') &&
        !key.includes('text') &&
        !key.includes('user')
      ) {
        sanitized[key] = value;
      }
    }

    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
  }
}

export const telemetry = new Telemetry();
